import { setMessages } from '@vartion/ui'
import { nextTick } from 'vue'
import { createI18n } from 'vue-i18n'

import type { Locale } from '~/types.ts'
import { onImportError } from './updateChecker.ts'

// Define the locales that we use. The label is shown in the interface for the user.
// Sorted by the labels.
export const locales: Record<Locale, string> = {
  'en-gb': 'English (UK)',
  en: 'English (US)',
  nl: 'Dutch',
  fr: 'French',
  hi: 'Hindi',
  it: 'Italian',
  de: 'German',
  el: 'Greek',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  es: 'Spanish',
  tr: 'Turkish',
} as const

// Create a map of locales for dayjs to use.
// Otherwise vite is not able to determine which locales should be tree shaken.
const loadDayjsLocale: Record<Locale, () => Promise<Record<string, unknown>>> = {
  'en-gb': async () => await import('dayjs/locale/en-gb.js'),
  en: async () => await import('dayjs/locale/en.js'),
  nl: async () => await import('dayjs/locale/nl.js'),
  fr: async () => await import('dayjs/locale/fr.js'),
  hi: async () => await import('dayjs/locale/hi.js'),
  it: async () => await import('dayjs/locale/it.js'),
  de: async () => await import('dayjs/locale/de.js'),
  el: async () => await import('dayjs/locale/el.js'),
  pt: async () => await import('dayjs/locale/pt.js'),
  ro: async () => await import('dayjs/locale/ro.js'),
  ru: async () => await import('dayjs/locale/ru.js'),
  es: async () => await import('dayjs/locale/es.js'),
  tr: async () => await import('dayjs/locale/tr.js'),
} as const

export const instance = createI18n({
  legacy: false,
  locale: '', // Locale will be filled in by `setLocale` in `window.localePromise`.
  fallbackLocale: 'en-gb',
  missingWarn: false,
  fallbackWarn: false,
})
export const i18n = instance.global

// Map UI messages to Pascal translation keys once; the translator follows the current locale.
setMessages({
  'Add another {repeatable}': 'add-another-repeatable-item',
  'Add answer option': 'add-answer-option',
  'Add follow up question': 'add-follow-up-question',
  'Add question': 'add-question',
  'Add section': 'add-section',
  'Add subtitle': 'add-subtitle',
  'Allow multiple answers': 'allow-multiple-answers',
  'Allow third option': 'allow-third-option',
  Answer: 'answer',
  'Answer options': 'answer-options',
  'Are you sure you want to remove "{title}"?': 'are-you-sure-you-want-to-remove-title',
  Cancel: 'cancel',
  Clear: 'clear',
  Confirm: 'confirm',
  'Date picker': 'date-picker',
  Description: 'description',
  'Drag and drop': 'drag-and-drop',
  'Drop down': 'drop-down',
  'Enter the description': 'enter-the-description',
  'Enter the question': 'enter-the-question',
  'Enter the subtitle': 'enter-the-subtitle',
  'Enter title': 'enter-title',
  'File upload': 'file-upload',
  'Include description': 'include-description',
  Internal: 'internal',
  'Invalid input': 'invalid-input',
  'Name of repeatable item': 'name-of-repeatable-item',
  No: 'no',
  'No data': 'no-data',
  'No results found': 'no-results-found',
  'No results found for your input': 'no-results-found-for-your-input',
  Number: 'number',
  Optional: 'optional',
  Paragraph: 'paragraph',
  Question: 'question',
  Remove: 'remove',
  'Remove all': 'remove-all',
  Rename: 'rename',
  Repeater: 'repeater',
  Sections: 'sections',
  'Select all': 'add-all',
  'Short answer': 'short-answer',
  'Show less': 'show-less',
  'Show more': 'show-more',
  'Start by adding your first question': 'start-by-adding-your-first-question',
  Subtitle: 'subtitle',
  'Suggested {label}': 'suggested-label',
  'This button is disabled because:': 'this-button-is-disabled-because',
  'This field can not be longer than {max} characters': 'this-field-can-not-be-longer-than-max-characters',
  'This field does not match {match}': 'this-field-does-not-match',
  'This field is required': 'this-field-is-required',
  'This field matches {mismatch}': 'this-field-does-not-match',
  'This field must be at least {min} characters long': 'this-field-must-be-at-least-min-characters-long',
  'This field must be at most {max}': 'this-field-must-not-be-larger-than-max',
  'This field must be equal to {gte} or greater': 'this-field-must-be-equal-to-gte-or-greater',
  'This field must be equal to {lte} or less': 'this-field-must-be-equal-to-lte-or-less',
  'This field must be greater than {gt}': 'this-field-must-be-greater-than-gt',
  'This field must be least {min}': 'this-field-must-not-be-smaller-than-min',
  'This field must be less than {lt}': 'this-field-must-be-less-than-lt',
  'This field must be one of: {in}': 'this-field-must-be-one-of-in',
  'This field must be type {type}': 'this-field-must-be-type-type',
  'This file is too large. Max filesize is: {size}': 'this-file-is-too-large-max-filesize-is-size',
  'This file is too small. Min filesize is: {size}': 'this-file-is-too-small-min-filesize-is-size',
  'True / False': 'true-false',
  URL: 'url',
  'User can enter a URL': 'user-can-enter-a-url',
  'User can enter a number': 'user-can-enter-a-number',
  'User can pick a date': 'user-can-pick-a-date',
  'User can type long text in a text area': 'user-can-type-long-text-in-a-text-area',
  'User can type text in a text area': 'user-can-type-text-in-a-text-area',
  'User can upload or drag file here': 'user-can-upload-or-drag-file-here',
  Yes: 'yes',
  'You have reached the maximum number of files allowed. Some of the files you have selected were not added.':
    'you-have-reached-the-maximum-number-of-files-allowed-some-of-the-files-you-have-selected-were-not-added',
  'click to upload': 'click-to-upload',
  optional: 'optional',
  or: 'or',
  required: 'required',
  '{count} / page': 'count--page',
  '{count} question requires attention | {count} questions require attention': 'count-questions-require-attention',
  '{from} to {to} of {total}': 'pagination-info',
})

let hasLocale = false

/**
 * Set the current application language.
 * @param locale The locale key of the language that should be set.
 */
export async function setLocale(locale: Locale) {
  if (hasLocale && locale === i18n.locale.value) {
    return
  }

  const alreadyLoading = stores.loading.app
  if (!alreadyLoading) {
    await stores.loading.update({ app: true })
  }

  // Set application locale.
  try {
    const [messages] = await Promise.all([import(`../locales/${locale}.json`), loadDayjsLocale[locale]()])
    i18n.setLocaleMessage(locale, messages.default)
    // Set dayjs locale.
    dayjs.locale(locale)
  } catch (error) {
    onImportError(error as Error)
  }
  i18n.locale.value = locale

  document.documentElement.lang = locale

  if (!alreadyLoading) {
    await stores.loading.update({ app: false })
  }

  hasLocale = true

  return nextTick()
}

type TranslateParams = Parameters<typeof i18n.t>
export function tk<TKey = TranslateParams[0]>(key: TKey, plural?: TranslateParams[1], options?: TranslateParams[2]) {
  const kebabKey = kebab(String(key)).toLowerCase()
  if (!i18n.te(kebabKey)) {
    return key
  }

  if (!plural) {
    return i18n.t(kebabKey)
  }

  if (!options) {
    return i18n.t(kebabKey, plural)
  }

  return i18n.t(kebabKey, plural, options)
}
